import { Button } from '@eon-home/react-library';
import {
    useElectricCarInformation,
    useEvSettings,
    useWallboxDeviceConfiguration,
} from '@store/selectors';
import { Routes } from '@tools/enums';
import { FC, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import './index.scss';
import { useSmartChargingPrompt } from '../../hooks';
import { useInternalRouting } from '@tools/utils';
import { useLocation } from 'react-router';

interface Props {
    isManualTrigger?: boolean;
}

export const EmobilitySmartChargingFlowChoicePrompt: FC<Props> = ({
    isManualTrigger = false,
}) => {
    const { t } = useTranslation();
    const goTo = useInternalRouting();
    const { pathname } = useLocation();
    const hasEvSettings = useEvSettings();
    const hasElectricCarInfo = useElectricCarInformation();
    const wallboxDeviceConfiguration = useWallboxDeviceConfiguration();

    const {
        showEmobilityChargingPrompt,
        showEmobilitySmartChargingEvSettingsPrompt,
        closePrompt,
    } = useSmartChargingPrompt();

    const onManualConnectClick = useCallback(() => {
        if (!pathname.includes(Routes.EMOBILITY)) {
            goTo(Routes.EMOBILITY);
        }

        if (hasEvSettings || hasElectricCarInfo) {
            showEmobilityChargingPrompt();
            return;
        }

        showEmobilitySmartChargingEvSettingsPrompt();
    }, [
        goTo,
        hasElectricCarInfo,
        hasEvSettings,
        pathname,
        showEmobilityChargingPrompt,
        showEmobilitySmartChargingEvSettingsPrompt,
    ]);

    const onConnectCarClick = useCallback(() => {
        closePrompt();
        goTo(Routes.CONNECT_ELECTRIC_CAR);
    }, [goTo, closePrompt]);

    return (
        <>
            <header className="c-charging-mode__title">
                {t('Smart charging')}
            </header>
            <p>
                <Trans
                    defaults="To best optimize your smart charging session, let us know what your <b>current battery level</b> is."
                    components={{
                        b: <b />,
                    }}
                />
                {wallboxDeviceConfiguration?.userSoc && !isManualTrigger && (
                    <>
                        {' '}
                        <Trans
                            defaults="If you skip this, your previous input of {{ userSoc }}% will be used which could lead to incorrect charging behavior."
                            values={{
                                userSoc: Math.round(
                                    wallboxDeviceConfiguration.userSoc,
                                ),
                            }}
                        />
                    </>
                )}
            </p>
            <p>
                {t(
                    'If you want to avoid manual entering for the future, connect now your car with us.',
                )}
            </p>
            <div className="c-charging-mode__buttons">
                <Button
                    small
                    outline
                    type="button"
                    onClick={onManualConnectClick}
                    className="c-btn--default"
                >
                    {t('Continue Manual')}
                </Button>
                <Button
                    type="button"
                    small
                    outline
                    className="c-btn--default"
                    onClick={onConnectCarClick}
                >
                    {t('Connect Car')}
                </Button>
            </div>
        </>
    );
};

export default EmobilitySmartChargingFlowChoicePrompt;
